import type { Axios } from "axios";
import type {
  BackendFilters,
  DataGridFilter,
  FilterLogicalOperator,
} from "../../utils/backendFilters";
import type { LiveChanges } from "../LiveSync/useLiveListSync";

export interface InsuranceVerificationSearchParams {
  practiceIds: string[];
  tab: string;
  page: number;
  pageSize: number;
  sortField: string | null;
  sortDirection: "asc" | "desc";
  filters: BackendFilters;
  devMode: boolean;
  includeAutoFiltered: boolean;
}

const toSearchRequest = (params: InsuranceVerificationSearchParams) => {
  const requestBody: {
    filters: Record<string, (string | null)[]>;
    page: number;
    page_size: number;
    sort: string[];
    sort_direction: string;
    tab: string;
    devmode: boolean;
    include_auto_filtered: boolean;
    datagrid_filters?: DataGridFilter[];
    logical_operator?: FilterLogicalOperator;
  } = {
    filters: params.practiceIds.length
      ? { practice_id: params.practiceIds }
      : {},
    page: params.page,
    page_size: params.pageSize,
    sort: [
      params.sortField ||
        (params.tab === "ALL" ? "posted_date_time" : "appointment_datetime"),
    ],
    sort_direction: params.sortDirection,
    tab: params.tab,
    devmode: params.devMode,
    include_auto_filtered: params.includeAutoFiltered,
  };

  if (params.filters.datagridFilters) {
    requestBody.datagrid_filters = params.filters.datagridFilters;
  }
  if (params.filters.logicalOperator) {
    requestBody.logical_operator = params.filters.logicalOperator;
  }
  return requestBody;
};

export const searchInsuranceVerifications = (
  c: Axios,
  params: InsuranceVerificationSearchParams,
  signal?: AbortSignal,
) =>
  c
    .post<SearchResponse>(
      "/insurance-verification/search",
      toSearchRequest(params),
      { signal },
    )
    .then((r) => r.data);

export const fetchInsuranceVerificationChanges = (
  c: Axios,
  ids: string[],
  params: InsuranceVerificationSearchParams,
): Promise<LiveChanges<InsuranceVerificationMessage>> =>
  c
    .post<{ rows: InsuranceVerificationMessage[]; matching_ids: string[] }>(
      "/insurance-verification/search/changes",
      { ids, view: toSearchRequest(params) },
    )
    .then((r) => ({ rows: r.data.rows, matchingIds: r.data.matching_ids }));
